import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { SharedModule } from "src/shared/shared.module";
import { environment } from "src/environments/environment";

/** One approved review, as `/course_reviews/random` returns it. */
interface PublicReview {
  id: number;
  review?: string;
  score?: number;
  student?: {
    nickname?: string;
    full_name?: string;
    academy?: string;
    image_thumb_url?: string;
    image_url?: string;
  };
}

/**
 * What learners say.
 *
 * The customer build shipped two named students, their photographs and their
 * testimonials about a named teacher, hard-coded in a mock-data file. None of
 * that can appear on a public demo — it would present a real school's students
 * as this site's own. The reviews now come from the API
 * (`/course_reviews/random`: approved, five-star, chosen server-side), so the
 * demo shows its own seeded content and nobody real is on the page.
 *
 * An empty list hides the whole band rather than leaving a heading over nothing.
 * Shown as a three-up grid rather than a carousel: three quotes at once read as
 * "people use this", one rotating card reads as "we found one".
 */
@Component({
  selector: "app-testimonials",
  imports: [SharedModule, CommonModule, TranslateModule],
  templateUrl: "./testimonials.component.html",
  styleUrl: "./testimonials.component.scss",
})
export class TestimonialsComponent implements OnInit, OnDestroy, AfterViewInit {
  #http = inject(HttpClient);

  fileUrl = environment.apiFileUrl;

  activeTestimonials = signal<number>(0);
  reviews = signal<PublicReview[]>([]);

  ngOnInit(): void {
    this.#http.get<PublicReview[]>("/course_reviews/random").subscribe({
      // Three, not six. Six came back as two rows in which the second repeated
      // the first almost word for word — the seed has three approved reviews —
      // and a wall of the same sentence twice is worse than one honest row.
      next: (rows) => this.reviews.set(Array.isArray(rows) ? dedupe(rows).slice(0, 3) : []),
      error: () => this.reviews.set([]),
    });
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {}

  name(item: PublicReview): string {
    return item.student?.nickname || item.student?.full_name || "ผู้เรียน";
  }

  educated(item: PublicReview): string {
    return item.student?.academy || "";
  }

  avatar(item: PublicReview): string {
    // The API returns storage paths relative to ITS host, not the app's. Without
    // the prefix the browser asks the static server for
    // /rails/active_storage/... and every avatar renders broken.
    const path = item.student?.image_thumb_url || item.student?.image_url;
    return path ? `${this.fileUrl}${path}` : "";
  }

  /** The one-letter monogram behind a reviewer's name. */
  monogram(item: PublicReview): string {
    return (this.name(item) || "?").trim().charAt(0);
  }

  getRatingArray(rating: number): number[] {
    return Array(rating).fill(0);
  }
}

/** Same words twice is one review, however many rows the API returned. */
function dedupe(rows: PublicReview[]): PublicReview[] {
  const seen = new Set<string>();
  return rows.filter((r) => {
    const key = (r.review || "").trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
